import { useState } from 'react';
import { toast } from 'sonner';

interface UseCopyToClipboardOptions {
  errorMessage?: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  successMessage?: string;
}

interface UseCopyToClipboardReturn {
  copied: boolean;
  copyToClipboard: (text: string, callbacks?: CopyToClipboardCallbacks) => Promise<void>;
}

interface CopyToClipboardCallbacks {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Custom hook for copying text to clipboard with fallback support for Office add-ins
 *
 * This hook handles clipboard operations in environments where the modern Clipboard API
 * might be restricted (like Office add-in iframes) by providing a fallback to execCommand.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing copied state and copyToClipboard function
 */
export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardReturn {
  const {
    errorMessage = 'Failed to copy to clipboard. Please try again.',
    onError,
    onSuccess,
    successMessage = 'Copied to clipboard',
  } = options;

  const [copied, setCopied] = useState(false);

  const handleSuccess = (callbacks?: CopyToClipboardCallbacks) => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(successMessage);
    onSuccess?.();
    callbacks?.onSuccess?.();
  };

  const copyToClipboard = async (
    text: string,
    callbacks?: CopyToClipboardCallbacks,
  ): Promise<void> => {
    try {
      const isHtml = /<[^>]+>/.test(text);

      // 1. Try modern clipboard API first
      if (navigator.clipboard) {
        try {
          if (isHtml && window.ClipboardItem) {
            // High-quality rich text copy
            const plainText = text
              .replaceAll(/<br\s*\/?>/gi, '\n')
              .replaceAll('</p>', '\n\n')
              .replaceAll(/<[^>]+>/g, '')
              .trim();

            const data = [
              new ClipboardItem({
                'text/html': new Blob([text], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
              }),
            ];
            await navigator.clipboard.write(data);
          } else {
            // Standard plain text copy
            await navigator.clipboard.writeText(text);
          }
          handleSuccess(callbacks);
          return;
        } catch (error) {
          console.warn('Modern clipboard API failed, trying fallback:', error);
        }
      }

      // 2. Fallback for Office.js environment
      if (isHtml) {
        // Fallback for rich text
        const container = document.createElement('div');
        container.innerHTML = text;
        container.style.position = 'fixed';
        container.style.left = '-999999px';
        container.style.top = '-999999px';
        container.style.userSelect = 'all';
        container.style.whiteSpace = 'pre-wrap';
        document.body.append(container);

        const range = document.createRange();
        range.selectNodeContents(container);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        try {
          if (document.execCommand('copy')) {
            handleSuccess(callbacks);
          } else {
            toast.error(errorMessage);
          }
        } finally {
          selection?.removeAllRanges();
          document.body.removeChild(container);
        }
      } else {
        // Fallback for plain text
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.setAttribute('readonly', '');
        document.body.append(textArea);
        textArea.contentEditable = 'true';
        textArea.readOnly = false;

        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        textArea.setSelectionRange(0, text.length);
        textArea.focus();

        try {
          if (document.execCommand('copy')) {
            handleSuccess(callbacks);
          } else {
            toast.error(errorMessage);
          }
        } finally {
          selection?.removeAllRanges();
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error(errorMessage);
      onError?.(error as Error);
      callbacks?.onError?.(error as Error);
    }
  };

  return { copied, copyToClipboard };
}
